import React from "react";
import { useNavigate } from "react-router-dom";
import {
  FolderOpen,
  AlertCircle,
  SearchX,
  FileQuestion,
  Users,
  Package,
  RefreshCw,
  ArrowLeft,
  ExternalLink,
  Plus,
} from "lucide-react";

const SIZES = {
  small: {
    wrap: "min-h-[280px] py-8",
    card: "max-w-sm",
    iconBox: "h-9 w-9",
    iconPx: 16,
    title: "text-sm",
    message: "text-xs",
  },
  medium: {
    wrap: "min-h-[360px] py-10",
    card: "max-w-md",
    iconBox: "h-11 w-11",
    iconPx: 20,
    title: "text-[15px]",
    message: "text-[13px]",
  },
  large: {
    wrap: "min-h-[480px] py-14",
    card: "max-w-lg",
    iconBox: "h-14 w-14",
    iconPx: 26,
    title: "text-base",
    message: "text-sm",
  },
};

const ICONS = {
  alert: AlertCircle,
  search: SearchX,
  file: FileQuestion,
  users: Users,
  package: Package,
  folder: FolderOpen,
  "folder-open": FolderOpen,
};

const NoDataState = ({
  icon = "folder",
  title = "No Data Found",
  message = "There is no data to display at the moment.",
  showRefresh = true,
  showBack = true,
  backUrl = null,
  onBack = null,
  onRefresh = null,
  size = "medium",
  actionText = "Go Back",
  learnMoreUrl = null,
  learnMoreText = "Learn more",
  showCreate = false,
  createText = "Create New",
  onCreate = null,
}) => {
  const navigate = useNavigate();
  const s = SIZES[size] || SIZES.medium;
  // `icon` is a name from ICONS ("folder", "users", ...) or a lucide icon component.
  const IconComp = (typeof icon === "string" ? ICONS[icon] : icon) || FolderOpen;

  const handleBack = () => {
    if (onBack) onBack();
    else if (backUrl) navigate(backUrl);
    else navigate(-1);
  };

  const handleRefresh = () => {
    if (onRefresh) onRefresh();
    else window.location.reload();
  };

  const handleLearnMore = () => {
    if (!learnMoreUrl) return;
    if (/^https?:\/\//i.test(learnMoreUrl)) {
      window.open(learnMoreUrl, "_blank", "noopener,noreferrer");
    } else {
      navigate(learnMoreUrl);
    }
  };

  return (
    <div className={`flex w-full items-center justify-center px-4 ${s.wrap}`}>
      <div className={`flex flex-col items-center text-center ${s.card}`}>

        {/* Icon: tile filled with the brand primary colour, white icon */}
        <div
          aria-hidden="true"
          className={`flex items-center justify-center rounded-lg bg-gradient-to-br from-primary to-primary_hover text-white shadow-sm ${s.iconBox}`}
        >
          <IconComp size={s.iconPx} strokeWidth={2} />
        </div>

        {/* Title */}
        <h3 className={`mt-4 font-semibold tracking-tight text-text_primary ${s.title}`}>
          {title}
        </h3>

        {/* Message */}
        <p className={`mt-1.5 max-w-xs leading-relaxed text-text_secondary ${s.message}`}>
          {message}{" "}
          {learnMoreUrl && (
            <button
              onClick={handleLearnMore}
              className="ml-0.5 inline-flex items-center gap-1 align-baseline font-medium text-primary hover:text-primary_hover"
            >
              <span className="underline decoration-primary/30 underline-offset-2">
                {learnMoreText}
              </span>
              <ExternalLink size={12} className="shrink-0" />
            </button>
          )}
        </p>

        {/* Actions */}
        {(showBack || showRefresh || showCreate) && (
          <div className="mt-6 flex flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-center">
            {showCreate && (
              <button
                onClick={onCreate}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-white hover:bg-primary_hover transition-colors duration-150"
              >
                <Plus size={15} />
                <span>{createText}</span>
              </button>
            )}

            {showBack && (
              <button
                onClick={handleBack}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-white hover:bg-primary_hover transition-colors duration-150"
              >
                <ArrowLeft size={15} />
                <span>{actionText}</span>
              </button>
            )}

            {showRefresh && (
              <button
                onClick={handleRefresh}
                className="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-surface px-5 py-2.5 text-sm font-medium text-text_secondary hover:bg-surface_alt hover:text-text_primary transition-colors duration-150"
              >
                <RefreshCw size={14} />
                <span>Refresh</span>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default NoDataState;
